import { useState, useRef, useEffect, useCallback } from 'react'
import { ChevronDown, X } from 'lucide-react'
import './StatusFilter.css'

interface StatusFilterProps {
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  label?: string
  /** Analytics event name prefix (e.g. `bug-status-filter`) applied to the trigger/clear/options. */
  analyticsPrefix?: string
  /** data-testid prefix so multiple filters (status/kind/severity) on one toolbar stay distinct. */
  testId?: string
}

export default function StatusFilter({ value, onChange, options, label = 'Status', analyticsPrefix = 'status-filter', testId = 'status-filter' }: StatusFilterProps) {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      const currentIndex = options.findIndex(o => o.value === value)
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0)
    }
  }, [open, options, value])

  useEffect(() => {
    if (open && focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex]?.focus()
    }
  }, [open, focusedIndex])

  const closeAndFocusTrigger = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  function handleTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) setOpen(true)
    } else if (e.key === 'Escape' && open) {
      e.preventDefault()
      closeAndFocusTrigger()
    }
  }

  function handleOptionKeyDown(e: React.KeyboardEvent, index: number) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(Math.min(index + 1, options.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (index === 0) {
          closeAndFocusTrigger()
        } else {
          setFocusedIndex(index - 1)
        }
        break
      case 'Home':
        e.preventDefault()
        setFocusedIndex(0)
        break
      case 'End':
        e.preventDefault()
        setFocusedIndex(options.length - 1)
        break
      case 'Escape':
        e.preventDefault()
        closeAndFocusTrigger()
        break
    }
  }

  const selectedLabel = options.find(o => o.value === value)?.label ?? label
  const listboxId = `${label.toLowerCase().replace(/\s+/g, '-')}-filter-listbox`

  return (
    <div className="status-filter" ref={ref} data-testid={testId}>
      <div className="status-filter__controls">
        <button
          ref={triggerRef}
          className={`status-filter__trigger ${value !== 'all' ? 'status-filter__trigger--active' : ''}`}
          onClick={() => setOpen(o => !o)}
          onKeyDown={handleTriggerKeyDown}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          data-testid={`${testId}-trigger`}
          data-analytics-event={`${analyticsPrefix}-toggle`}
        >
          <span>{value !== 'all' ? selectedLabel : label}</span>
          {value === 'all' && (
            <ChevronDown size={14} aria-hidden="true" data-testid={`${testId}-chevron`} />
          )}
        </button>
        {value !== 'all' && (
          <button
            className="status-filter__clear"
            onClick={() => onChange('all')}
            aria-label={`Clear ${label.toLowerCase()} filter`}
            data-testid={`${testId}-clear`}
            data-analytics-event={`${analyticsPrefix}-clear`}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      {open && (
        <div
          className="status-filter__dropdown"
          role="listbox"
          id={listboxId}
          aria-label={`${label} filter options`}
          data-testid={`${testId}-dropdown`}
        >
          {options.map((opt, index) => (
            <button
              key={opt.value}
              ref={el => { optionRefs.current[index] = el }}
              className={`status-filter__option ${opt.value === value ? 'status-filter__option--selected' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              onKeyDown={(e) => handleOptionKeyDown(e, index)}
              role="option"
              aria-selected={opt.value === value}
              data-testid={`${testId}-option-${opt.value}`}
              data-analytics-event={`${analyticsPrefix}-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
